import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el, en } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Transcript {
  id: string;
  title: string;
  meeting_date?: string;
  file_name?: string;
  content?: string;
  ai_processed: boolean;
  ai_processed_at?: string;
  uploaded_by_name?: string;
  created_at: string;
}

const TranscriptModule: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.transcript;
  const C = M.common;
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState<Transcript | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', meetingDate: '', meetingId: '' });
  const [meetingOptions, setMeetingOptions] = useState<{ id: string; title: string; meeting_date: string | null }[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Transcript | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headers = { Authorization: `Bearer ${token}` };

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchTranscripts = async () => {
    try {
      const res = await axios.get(`${API}/advisor/${project.id}/transcripts`, { headers });
      const data: Transcript[] = res.data || [];
      setTranscripts(data);
      // Stop polling when all transcripts are processed
      if (pollingRef.current && data.every(t => t.ai_processed)) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchTranscripts();
    axios.get(`${API}/meetings/${project.id}`, { headers })
      .then(res => setMeetingOptions((res.data || []).map((m: any) => ({ id: m.id, title: m.title, meeting_date: m.meeting_date }))))
      .catch(() => {});
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  // Start polling whenever a pending transcript exists
  useEffect(() => {
    const hasPending = transcripts.some(t => !t.ai_processed);
    if (hasPending && !pollingRef.current) {
      pollingRef.current = setInterval(fetchTranscripts, 4000);
    } else if (!hasPending && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [transcripts]);

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.title) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('meetingDate', uploadForm.meetingDate);
      formData.append('uploadedByName', user.full_name);
      formData.append('userId', user.id);
      if (uploadForm.meetingId) formData.append('meetingId', uploadForm.meetingId);

      const res = await axios.post(
        `${API}/advisor/${project.id}/transcripts`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );

      setTranscripts(prev => [res.data, ...prev]);
      setShowUploadModal(false);
      setUploadForm({ title: '', meetingDate: '', meetingId: '' });
      setSelectedFile(null);
    } catch (err) { console.error(err); }
    finally { setUploading(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/advisor/${project.id}/transcripts/${deleteTarget.id}`, { headers });
      setTranscripts(prev => prev.filter(t => t.id !== deleteTarget.id));
      if (selectedTranscript?.id === deleteTarget.id) setSelectedTranscript(null);
    } catch (err) { console.error(err); }
    finally { setDeleteTarget(null); }
  };

  const formatDate = (d?: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(C.dateLocale);
  };

  const formatTimeAgo = (d: string) => {
    const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (m < 1) return L.timeJustNow;
    if (m < 60) return L.timeMinutes(m);
    const h = Math.floor(m / 60);
    if (h < 24) return L.timeHours(h);
    return new Date(d).toLocaleDateString(C.dateLocale);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Header */}
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={{ padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky', top: 64, zIndex: 99 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{project.name}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#374151', fontWeight: '500' }}>{L.pageTitle}</span>
      </div>

      <div style={{ padding: '32px 48px', boxSizing: 'border-box' as const }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>{L.pageTitle}</h2>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.subtitle}</p>
          </div>
          {canEdit && (
            <button onClick={() => setShowUploadModal(true)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
              {L.newTranscript}
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px', alignItems: 'start' }}>
          {/* Sidebar — λίστα transcripts */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Repository ({transcripts.length})</span>
            </div>
            {loading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>{C.loading}</div>
            ) : transcripts.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.emptyTitle}</p>
              </div>
            ) : (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {transcripts.map(t => (
                  <div key={t.id} onClick={() => setSelectedTranscript(t)} style={{ padding: '12px 16px', borderBottom: '1px solid #f9fafb', cursor: 'pointer', backgroundColor: selectedTranscript?.id === t.id ? '#ede9fe' : 'white', borderLeft: selectedTranscript?.id === t.id ? '3px solid #4f46e5' : '3px solid transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {t.meeting_date && `📅 ${formatDate(t.meeting_date)} · `}
                          {formatTimeAgo(t.created_at)}
                        </div>
                        <div style={{ marginTop: '6px', display: 'flex', gap: '4px' }}>
                          {t.ai_processed ? (
                            <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '999px', backgroundColor: '#dcfce7', color: '#166534', fontWeight: '500' }}>{L.aiProcessed}</span>
                          ) : (
                            <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '999px', backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: '500' }}>{L.aiProcessing}</span>
                          )}
                        </div>
                      </div>
                      {canEdit && (
                        <button onClick={e => { e.stopPropagation(); setDeleteTarget(t); }} style={{ backgroundColor: 'transparent', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '14px', flexShrink: 0, padding: '2px' }}>🗑</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main — περιεχόμενο transcript */}
          {selectedTranscript ? (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>{selectedTranscript.title}</h3>
                  <div style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', gap: '12px' }}>
                    {selectedTranscript.meeting_date && <span>📅 {formatDate(selectedTranscript.meeting_date)}</span>}
                    {selectedTranscript.uploaded_by_name && <span>👤 {selectedTranscript.uploaded_by_name}</span>}
                    {selectedTranscript.file_name && <span>📎 {selectedTranscript.file_name}</span>}
                  </div>
                </div>
                {selectedTranscript.ai_processed ? (
                  <span style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#dcfce7', color: '#166534', fontWeight: '500' }}>{L.aiProcessed}</span>
                ) : (
                  <span style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: '500' }}>{L.aiProcessingLong}</span>
                )}
              </div>
              <div style={{ padding: '20px', maxHeight: '500px', overflowY: 'auto' }}>
                {selectedTranscript.content ? (
                  <pre style={{ fontSize: '13px', color: '#374151', lineHeight: '1.7', whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                    {selectedTranscript.content}
                  </pre>
                ) : (
                  <p style={{ color: '#9ca3af', fontSize: '13px' }}>{L.noContent}</p>
                )}
              </div>
              {selectedTranscript.ai_processed && (
                <div style={{ padding: '12px 20px', backgroundColor: '#f0fdf4', borderTop: '1px solid #dcfce7', fontSize: '13px', color: '#166534' }}>
                  {L.aiNote}
                </div>
              )}
            </div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
              <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>{L.selectHint}</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && canEdit && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '480px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 20px' }}>{L.uploadModalTitle}</h2>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>{L.fieldTitle}</label>
              <input
                style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none' }}
                value={uploadForm.title}
                onChange={e => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder={L.fieldTitlePlaceholder}
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>{L.fieldDate}</label>
              <input
                type="date"
                style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none' }}
                value={uploadForm.meetingDate}
                onChange={e => setUploadForm({ ...uploadForm, meetingDate: e.target.value })}
              />
            </div>

            {meetingOptions.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>{L.fieldMeeting}</label>
                <select
                  style={{ width: '100%', padding: '9px 10px', borderRadius: '7px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box' as const, outline: 'none', backgroundColor: 'white' }}
                  value={uploadForm.meetingId}
                  onChange={e => setUploadForm({ ...uploadForm, meetingId: e.target.value })}
                >
                  <option value="">{L.fieldMeetingNone}</option>
                  {meetingOptions.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.title}{m.meeting_date ? ` — ${new Date(m.meeting_date).toLocaleDateString(C.dateLocale)}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>{L.fieldFile}</label>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) setSelectedFile(f); }}
                onClick={() => fileInputRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? '#4f46e5' : '#e5e7eb'}`, borderRadius: '8px', padding: '20px', textAlign: 'center', cursor: 'pointer', backgroundColor: dragOver ? '#ede9fe' : '#fafafa' }}
              >
                <input ref={fileInputRef} type="file" accept=".txt,.pdf,.docx" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) setSelectedFile(f); }} />
                {selectedFile ? (
                  <div>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>📎</div>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#4f46e5' }}>{selectedFile.name}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{(selectedFile.size / 1024).toFixed(1)} KB</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '24px', marginBottom: '6px' }}>📄</div>
                    <div style={{ fontSize: '13px', color: '#374151' }}>{L.dropHint}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>TXT, PDF, DOCX</div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ backgroundColor: '#ede9fe', borderRadius: '8px', padding: '10px 14px', marginBottom: '20px', fontSize: '12px', color: '#4f46e5' }}>
              {L.aiUploadNote}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowUploadModal(false); setSelectedFile(null); setUploadForm({ title: '', meetingDate: '', meetingId: '' }); }} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '8px 18px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={handleUpload} disabled={!selectedFile || !uploadForm.title || uploading} style={{ backgroundColor: selectedFile && uploadForm.title ? '#4f46e5' : '#e5e7eb', color: selectedFile && uploadForm.title ? 'white' : '#9ca3af', border: 'none', padding: '8px 18px', borderRadius: '7px', fontSize: '13px', fontWeight: '600', cursor: selectedFile && uploadForm.title ? 'pointer' : 'not-allowed' }}>
                {uploading ? L.uploading : L.uploadBtn}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px', width: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 22 }}>🗑</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px', textAlign: 'center' }}>{L.deleteTitle}</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px', textAlign: 'center' }}>{L.deleteSubtitle}</p>
            <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', marginBottom: 24 }}>
              <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Transcript</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', margin: 0 }}>{deleteTarget.title}</p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteTarget(null)} style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: 'transparent', color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: 8, backgroundColor: '#ef4444', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{L.deleteConfirm}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptModule;
